<script setup lang="ts">
const props = defineProps<{
  minigame: TMiniGameTemplate;
}>();

const { playMiniGame } = useSmarticoMiniGames();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();

const buyInCost = computed(() => {
  if (props.minigame.buyin_cost_points) {
    const value = props.minigame.buyin_cost_points;

    return {
      value,
      icon: 'currency-header/coin',
      name: value === 1 ? 'COIN' : 'COINS',
      color: '#FF9B50',
    };
  }
  if (props.minigame.buyin_cost_diamonds) {
    const value = props.minigame.buyin_cost_diamonds;

    return {
      value,
      icon: 'currency-header/diamond',
      name: value === 1 ? 'DIAMOND' : 'DIAMONDS',
      color: '#00C9FF',
    };
  }
  if (props.minigame.buyin_cost_gems) {
    const value = props.minigame.buyin_cost_gems;

    return {
      value,
      icon: 'currency-header/gem',
      name: value === 1 ? 'GEM' : 'GEMS',
      color: '#81FFA1',
    };
  }

  return null;
});

const buttonText = computed(() => {
  switch (props.minigame.saw_game_type) {
    case 'giftbox':
      return 'ABRIR';

    case 'scratch':
      return 'RASPAR';

    case 'matchx':
      return 'PALPITAR';

    case 'quiz':
      return 'RESPONDER';

    case 'wheel':
      return 'GIRAR';

    case 'coin_flip':
      return 'JOGAR';

    default:
      return 'GIRAR';
  }
});

const buttonIcon = computed(() => {
  switch (props.minigame.saw_game_type) {
    case 'giftbox':
      return 'mini-games/abrir';

    case 'scratch':
      return 'mini-games/raspadinha-coin';

    case 'matchx':
      return 'messages';

    case 'quiz':
      return 'pen_square';

    case 'wheel':
      return 'mini-games/girar';

    case 'coin_flip':
      return 'mini-games/girar';

    default:
      return 'mini-games/girar';
  }
});

async function handlePlayMiniGame() {
  if (!isAuthenticated.value) {
    openLogin();

    return;
  }
  await playMiniGame(props.minigame.id);
}
</script>

<template>
  <div
    v-if="minigame.saw_game_type !== 'matchx'"
    class="minigame-card"
  >
    <!-- Header: Imagem + Info -->
    <div class="minigame-card__header">
      <SmarticoApostatudoSmarticoImage
        :alt="minigame.name"
        class="minigame-card__image"
        :src="minigame.promo_image"
      />
      <div class="minigame-card__info">
        <div class="minigame-card__title">
          {{ minigame.name }}
        </div>
        <div class="minigame-card__description">
          {{ minigame.promo_text }}
        </div>
      </div>
    </div>

    <!-- Preço -->
    <div class="minigame-card__price">
      <NuxtIcon
        v-if="buyInCost"
        class="minigame-card__price-icon"
        :filled="true"
        :name="buyInCost.icon"
      />
      <span
        v-if="buyInCost"
        class="minigame-card__price-value"
        :style="{ color: buyInCost.color }"
      >
        {{ buyInCost.value.toLocaleString('pt-BR') }} {{ buyInCost.name }}
      </span>
      <span
        v-else
        class="minigame-card__price-value minigame-card__price-value--free"
      >
        GRÁTIS
      </span>
    </div>

    <!-- Botão -->
    <button
      class="minigame-card__button"
      @click="handlePlayMiniGame"
    >
      <NuxtIcon
        :filled="true"
        :name="buttonIcon"
      />
      <span>{{ buttonText }}</span>
    </button>
  </div>
</template>

<style scoped>
.minigame-card {
  width: 212px;
  height: 160px;
  background: #010612;
  border: 1px solid #1d2329;
  border-radius: 10px;
  padding: 5px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 6px;
  user-select: none;
  box-sizing: border-box;
  overflow: hidden;
}

/* Header: Imagem + Info */
.minigame-card__header {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  min-height: 60px;
  max-height: 60px;
  overflow: hidden;
}

.minigame-card__image {
  width: 52px;
  height: 52px;
  border-radius: 8px;
  flex-shrink: 0;
  object-fit: cover;
}

.minigame-card__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}

.minigame-card__title {
  font-size: 12px;
  font-weight: 700;
  color: white;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  line-height: 1.25;
}

.minigame-card__description {
  font-size: 10px;
  font-weight: 400;
  color: #a1a1aa;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  line-height: 1.3;
}

/* Preço */
.minigame-card__price {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 8px;
  background: rgba(0, 201, 255, 0.1);
  border-radius: 8px;
  flex-shrink: 0;
}

.minigame-card__price-icon {
  font-size: 18px;
  flex-shrink: 0;
}

.minigame-card__price-value {
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.minigame-card__price-value--free {
  color: #00c9ff;
}

/* Botão */
.minigame-card__button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 100%;
  padding: 8px;
  background: var(--accent-color, #1414dd);
  border: none;
  border-radius: 4.5px;
  color: white;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.2s;
  flex-shrink: 0;
}

.minigame-card__button:hover {
  filter: brightness(1.1);
}
</style>
